/**
 * The states a page can actually be in, because three was not enough.
 *
 * `isLoading` in React Query v5 means `isPending && isFetching`. A query whose
 * retry is PAUSED is pending and not fetching, so `isLoading`, `isError` and
 * `data` are all falsy at once and a page branching on only those three renders
 * its header and then nothing.
 *
 * Pausing has nothing to do with the response. query-core gates a retry on
 * `focusManager.isFocused() && onlineManager.isOnline()` (retryer.js:42-43), so
 * a tab that is in the background when a retry comes due parks there until it
 * is focused again - which is ordinary behaviour, not an error path. Observed
 * live as `{status: "pending", fetchStatus: "paused", failureCount: 1}`
 * surviving a full reload and a manual refetch.
 *
 * "absent" is separate from "error" because a retry button is a lie when the
 * server has answered definitively. `/dashboard/student` returns 404 for any
 * student-role membership with no `lms_student` row; telling that person the
 * dashboard "could not load" invites them to press Try again forever. It is
 * opt-in per call site: a 404 from the admin dashboard would be a routing bug,
 * not a missing record, and must stay an error.
 */
export type LoadState = "loading" | "paused" | "error" | "absent" | "ready";

export interface QueryLike {
  data: unknown;
  isPending: boolean;
  isError: boolean;
  fetchStatus: "fetching" | "paused" | "idle";
  error?: unknown;
}

export interface LoadStateOptions {
  /** HTTP status that means "the server answered, and there is no such record". */
  absentOn?: number;
}

export function loadState(query: QueryLike, options: LoadStateOptions = {}): LoadState {
  // Data first. A background refetch that fails leaves `isError` true beside a
  // complete, correct dashboard; with `refetchInterval: 60_000` that would
  // paint "could not load" over a page the reader is already using.
  if (query.data !== undefined) return "ready";
  if (query.isError) {
    const status = (query.error as { status?: number } | undefined)?.status;
    return options.absentOn !== undefined && status === options.absentOn ? "absent" : "error";
  }
  if (query.isPending && query.fetchStatus === "paused") return "paused";
  return "loading";
}
